import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@wundo/ui';
import type { CalendarProxySettings } from '@wundo/shared/calendarProxy.ts';
import { getCalendarProxyStatus } from '@/lib/organisations.ts';
import { formatDateTime } from '@/utils/dateUtils.ts';

export interface CalendarProxyStatusPanelProps {
  organisationId: string;
}

function onOff(value: boolean): string {
  return value ? 'On' : 'Off';
}

/* The names of the fields the customer filled in when installing the Calendar Connector. */
function settingRows(settings: CalendarProxySettings): [string, string][] {
  return [
    ['Block external meetings', onOff(settings.block_external)],
    ['Block private meetings', onOff(settings.block_private)],
    [
      'Block keywords',
      settings.block_words.length > 0 ? settings.block_words.join(', ') : 'None',
    ],
  ];
}

/**
 * What the saved Calendar Connector reports, read-only. The customer sets these in their
 * own install; staff only see them, to know what Wundo receives.
 */
export function CalendarProxyStatusPanel({
  organisationId,
}: CalendarProxyStatusPanelProps) {
  const {
    data: status,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['organisation', organisationId, 'calendar-proxy'],
    queryFn: () => getCalendarProxyStatus(organisationId),
  });

  if (isPending) {
    return (
      <div className='flex items-center gap-2 text-sm text-ink-muted'>
        <Spinner />
        Asking the Calendar Connector…
      </div>
    );
  }

  if (isError || !status.reachable) {
    return (
      <p
        role='alert'
        className='rounded-md bg-danger-fill px-4 py-3 text-sm font-semibold text-danger'
      >
        The Calendar Connector does not answer. Calendars connected through it cannot be
        read until it does.
        {status?.answered_at
          ? ` It last answered on ${formatDateTime(status.answered_at)}.`
          : ''}
      </p>
    );
  }

  const rows: [string, string][] = [
    ['Version', status.version ?? '–'],
    ['Settings fingerprint', status.settings_hash ?? '–'],
    ...(status.settings ? settingRows(status.settings) : []),
    ['App registration (client id)', status.app_client_id ?? '–'],
    ['App registration tenant', status.app_tenant_id ?? '–'],
    ['Last answered', formatDateTime(status.answered_at)],
  ];

  return (
    <dl className='grid grid-cols-2 gap-x-6 gap-y-2 text-sm max-mobile:grid-cols-1'>
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt className='text-xs text-ink-muted'>{label}</dt>
          <dd className='break-all text-ink'>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
